"use client";

import json from "@/public/data/hotel/results.json";
import { cn } from "@/shared/libs/commonUtils";
import { CaretUp } from "@phosphor-icons/react";
import {
  type Dispatch,
  type MouseEventHandler,
  type ReactNode,
  type SetStateAction,
  forwardRef,
} from "react";
import Box from "./Box";
import { Button } from "./button";
import { Card } from "./Card";
import Separator from "./separator";
import { Tabs, TabsContent } from "./tabs";
import { useDeviceType } from "@/shared/context/DeviceContext";

interface IResultCardProps {
  firstChild: ReactNode | ReactNode[];
  className?: {
    card?: string;
    cardWrapper?: string;
    firstChildStyle?: string;
    secondChildStyle?: string;
  };

  secondChild?: ReactNode | ReactNode[];
  hasSeparator?: boolean;
  tab?: {
    tabContent: { name: string; content: ReactNode }[];
    activeTab: boolean;
    setActiveTab: Dispatch<SetStateAction<boolean>>;
  };
  handleClick?: MouseEventHandler<HTMLDivElement> | undefined;
}

const DataCard = forwardRef<HTMLDivElement, IResultCardProps>(
  (
    {
      firstChild,
      className,
      tab,
      secondChild,
      handleClick,
      hasSeparator = true,
      ...props
    },
    ref,
  ) => {
    const {
      body: { resultCard },
    } = json;

    return (
      <Tabs
        className={cn(
          "w-full bg-background border border-border! rounded-xs md:rounded-md  lg:rounded-xs! hover:shadow-sm",
          className?.card,
        )}
        dir="rtl"
        onClick={useDeviceType() === "mobile" ? handleClick : undefined}
      >
        <Card
          className={`flex bg-background justify-start h-full items-start border-0! ${
            tab?.activeTab && "border-b!"
          }  ${className?.cardWrapper}`}
          {...props}
          ref={ref}
        >
          <div
            className={`flex-1 w-full md:flex-[.75] ${className?.firstChildStyle}`}
          >
            {firstChild}
          </div>

          {secondChild && hasSeparator && (
            <>
              <Separator
                className="bg-muted! md:hidden"
                direction={"horizontal"}
              />
              <Separator
                className="bg-muted! max-md:hidden"
                direction={"vertical"}
              />
            </>
          )}

          {secondChild && (
            <div
              className={`flex-1 w-full md:flex-[.25] h-full ${className?.secondChildStyle}`}
            >
              {secondChild}
            </div>
          )}
        </Card>
        {tab?.activeTab && useDeviceType() === "desktop" && (
          <div className="inline w-full md:flex-[.75]">
            {tab?.tabContent.map(({ content, name }) => (
              <TabsContent
                key={name}
                value={name}
                className={`transition-height duration-1000 ease-in-out  !mt-0 ${
                  tab?.activeTab ? "h-auto" : "h-0"
                }`}
              >
                {content}

                <Button
                  variant="link"
                  className="text-primary hover:text-primary no-underline! gap-1 w-full"
                  onClick={() => tab.setActiveTab(false)}
                >
                  <Box className="flex gap-1 items-center hover:bg-primary-foreground rounded-full  px-3">
                    {resultCard.closeTabButtonLabel}
                    <CaretUp />
                  </Box>
                </Button>
              </TabsContent>
            ))}
          </div>
        )}
      </Tabs>
    );
  },
);

DataCard.displayName = "DataCard";

export default DataCard;
